import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  BookOpen,
  FileQuestion,
  Activity,
  Clock,
  UserPlus,
  ArrowUpRight,
  ArrowDownRight,
  FolderOpen,
  CalendarDays,
  Zap,
  Wallet,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { adminApi } from '../../services/api';
import { adminPaymentsApi, type AdminPaymentStats } from '@/payment';

interface DashboardStats {
  totalUsers: number;
  guestUsers: number;
  registeredUsers: number;
  activeUsers: number;
  totalSessions: number;
  totalQuestions: number;
  totalExams: number;
  totalCategories: number;
  newUsersToday: number;
  sessionsToday: number;
}

interface GrowthPoint {
  date: string;
  count: number;
}

interface ActivityPoint {
  date: string;
  sessions: number;
  averageScore: number;
}

interface RecentUser {
  id: string;
  name: string;
  email: string;
  role: string;
  createdAt: string;
  totalXp: number;
  level: number;
}

interface CategoryStat {
  categoryId: string;
  categoryName: string;
  count: number;
}

interface TopUser {
  id: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  totalXp: number;
  level: number;
  streak: number;
  sessions: number;
  completedSessions: number;
  averageScore: number;
}

interface PopularExam {
  examId: string;
  examName: string;
  icon: string | null;
  sessions: number;
  uniqueUsers: number;
  averageScore: number;
}

export function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [growth, setGrowth] = useState<GrowthPoint[]>([]);
  const [activity, setActivity] = useState<ActivityPoint[]>([]);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [categoryStats, setCategoryStats] = useState<CategoryStat[]>([]);
  const [topUsers, setTopUsers] = useState<TopUser[]>([]);
  const [popularExams, setPopularExams] = useState<PopularExam[]>([]);
  const [paymentStats, setPaymentStats] = useState<AdminPaymentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setError(null);
      const [dashRes, growthRes, activityRes, usersRes, catRes, topRes, examRes, payRes] = await Promise.all([
        adminApi.getDashboard(),
        adminApi.getUserGrowth(30).catch(() => ({ data: [] })),
        adminApi.getSessionActivity(30).catch(() => ({ data: [] })),
        adminApi.getRecentUsers(8).catch(() => ({ data: [] })),
        adminApi.getQuestionsByCategory().catch(() => ({ data: [] })),
        adminApi.getTopUsers(5).catch(() => ({ data: [] })),
        adminApi.getPopularExams(5).catch(() => ({ data: [] })),
        adminPaymentsApi.getStats(30).catch(() => null),
      ]);
      setStats(dashRes.data);
      setGrowth(growthRes.data || []);
      setActivity(activityRes.data || []);
      setRecentUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      setCategoryStats(Array.isArray(catRes.data) ? catRes.data : []);
      setTopUsers(Array.isArray(topRes.data) ? topRes.data : []);
      setPopularExams(Array.isArray(examRes.data) ? examRes.data : []);
      setPaymentStats(payRes ? payRes.data : null);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setStats(null);
      setError('Impossible de charger les statistiques du tableau de bord.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tableau de bord</h1>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={() => { setLoading(true); loadDashboard(); }}
            className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const totalNewUsers7d = growth.slice(-7).reduce((s, p) => s + p.count, 0);
  const recentActivity7d = activity.slice(-7);
  const totalSessions7d = recentActivity7d.reduce((s, p) => s + p.sessions, 0);
  // Score moyen PONDÉRÉ par le nombre de sessions de chaque jour : c'est la
  // vraie moyenne des scores de TOUTES les sessions de la période. Une moyenne
  // simple des moyennes quotidiennes fausserait le résultat (un jour à
  // 1 session pèserait autant qu'un jour à 20) — d'où l'écart apparent avec la
  // courbe « Sessions & score moyen ».
  const avgScoreVal =
    totalSessions7d > 0
      ? recentActivity7d.reduce((s, p) => s + p.averageScore * p.sessions, 0) /
        totalSessions7d
      : 0;

  const topCategories = [...categoryStats].sort((a, b) => b.count - a.count).slice(0, 8);
  const maxCatCount = topCategories.length > 0 ? topCategories[0].count : 1;

  // Séries temporelles continues (jours sans données remplis à 0) pour des
  // courbes lisibles même avec une activité clairsemée.
  const growthSeries = fillDailySeries(30, growth, (p: GrowthPoint) => ({ nouveaux: p.count }), { nouveaux: 0 });
  const activitySeries = fillDailySeries(
    30,
    activity,
    (p: ActivityPoint) => ({ sessions: p.sessions, score: p.sessions > 0 ? p.averageScore : null }),
    { sessions: 0, score: null },
  );
  const revenueSeries = paymentStats
    ? fillDailySeries(
        30,
        paymentStats.daily,
        (p: AdminPaymentStats['daily'][number]) => ({ usd: p.revenueUSD, cdf: p.revenueCDF, transactions: p.count }),
        { usd: 0, cdf: 0, transactions: 0 },
      )
    : [];
  const hasRevenueCDF = revenueSeries.some((p) => (p.cdf ?? 0) > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Tableau de bord
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Vue d'ensemble de votre plateforme
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <CalendarDays size={14} />
          {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      </div>

      {/* Primary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Utilisateurs"
          value={stats?.totalUsers || 0}
          icon={Users}
          color="bg-blue-500"
          subtext={`${stats?.registeredUsers ?? 0} inscrits · ${stats?.guestUsers ?? 0} invités`}
          trend={stats?.newUsersToday ? `+${stats.newUsersToday} aujourd'hui` : undefined}
          trendUp={true}
        />
        <StatCard
          title="Utilisateurs actifs"
          value={stats?.activeUsers || 0}
          icon={Activity}
          color="bg-emerald-500"
          subtext="7 derniers jours"
          trend={stats?.activeUsers && stats?.totalUsers ? `${Math.round((stats.activeUsers / stats.totalUsers) * 100)}% du total` : undefined}
          trendUp={true}
        />
        <StatCard
          title="Sessions"
          value={stats?.totalSessions || 0}
          icon={Clock}
          color="bg-violet-500"
          subtext={`${stats?.sessionsToday || 0} aujourd'hui`}
          trend={totalSessions7d > 0 ? `${totalSessions7d} cette semaine` : undefined}
          trendUp={true}
        />
        <StatCard
          title="Score moyen"
          value={Math.round(avgScoreVal)}
          icon={Zap}
          color="bg-amber-500"
          subtext="7 derniers jours"
          suffix="%"
        />
      </div>

      {/* Content Stats */}
      <div className="grid grid-cols-3 gap-4">
        <ContentCard
          title="Examens"
          value={stats?.totalExams || 0}
          icon={BookOpen}
          href="/admin/exams"
          color="text-indigo-500 bg-indigo-500/10"
        />
        <ContentCard
          title="Catégories"
          value={stats?.totalCategories || 0}
          icon={FolderOpen}
          href="/admin/categories"
          color="text-pink-500 bg-pink-500/10"
        />
        <ContentCard
          title="Questions"
          value={stats?.totalQuestions || 0}
          icon={FileQuestion}
          href="/admin/questions"
          color="text-emerald-500 bg-emerald-500/10"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Croissance utilisateurs</h2>
              <p className="text-xs text-gray-400 mt-0.5">30 derniers jours · {totalNewUsers7d} nouveaux cette semaine</p>
            </div>
            <div className="flex items-center gap-1 text-xs text-emerald-500 font-medium">
              <UserPlus size={14} />
              +{growth.length > 0 ? growth[growth.length - 1].count : 0} dernier jour
            </div>
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={growthSeries} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={30} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [v, 'Nouveaux utilisateurs']} />
                <Area type="monotone" dataKey="nouveaux" stroke="#10b981" strokeWidth={2} fill="url(#growthGrad)" dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Session Activity Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Sessions & score moyen</h2>
              <p className="text-xs text-gray-400 mt-0.5">30 derniers jours · {totalSessions7d} sessions cette semaine</p>
            </div>
            <div className="flex items-center gap-1 text-xs text-violet-500 font-medium">
              <Activity size={14} />
              {activity.length > 0 ? activity[activity.length - 1].sessions : 0} dernier jour
            </div>
          </div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={activitySeries} margin={{ top: 5, right: -15, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={30} />
                <YAxis yAxisId="sessions" allowDecimals={false} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                <YAxis yAxisId="score" orientation="right" domain={[0, 100]} tick={{ fontSize: 10, fill: '#f59e0b' }} tickLine={false} axisLine={false} unit="%" />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(v: number, name: string) =>
                    name === 'score' ? [`${Math.round(v)}%`, 'Score moyen'] : [v, 'Sessions']
                  }
                />
                <Bar yAxisId="sessions" dataKey="sessions" fill="#8b5cf6" opacity={0.75} radius={[3, 3, 0, 0]} maxBarSize={18} />
                <Line yAxisId="score" type="monotone" dataKey="score" stroke="#f59e0b" strokeWidth={2} dot={false} activeDot={{ r: 4 }} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Revenus des paiements</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              30 derniers jours
              {paymentStats && (
                <>
                  {' '}· {paymentStats.totals.success}/{paymentStats.totals.total} transactions réussies
                  {paymentStats.totals.revenueUSD > 0 && <> · <span className="text-emerald-500 font-medium">{paymentStats.totals.revenueUSD.toLocaleString('fr-FR')} $</span></>}
                  {paymentStats.totals.revenueCDF > 0 && <> · <span className="text-blue-500 font-medium">{paymentStats.totals.revenueCDF.toLocaleString('fr-FR')} FC</span></>}
                </>
              )}
            </p>
          </div>
          <Link to="/admin/payments/transactions" className="text-xs text-emerald-500 hover:text-emerald-600 font-medium flex items-center gap-1">
            <Wallet size={14} /> Gérer les transactions <ArrowUpRight size={12} />
          </Link>
        </div>
        {!paymentStats || paymentStats.totals.total === 0 ? (
          <div className="h-40 flex flex-col items-center justify-center text-gray-400 gap-1">
            <Wallet className="w-7 h-7 opacity-40" />
            <p className="text-xs">Aucune transaction sur la période</p>
          </div>
        ) : (
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueSeries} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="revUsdGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="revCdfGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={30} />
                <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={TOOLTIP_STYLE}
                  formatter={(v: number, name: string) =>
                    name === 'usd' ? [`${v.toLocaleString('fr-FR')} $`, 'Revenus USD']
                    : name === 'cdf' ? [`${v.toLocaleString('fr-FR')} FC`, 'Revenus CDF']
                    : [v, 'Transactions']
                  }
                />
                <Area type="monotone" dataKey="usd" stroke="#10b981" strokeWidth={2} fill="url(#revUsdGrad)" dot={false} activeDot={{ r: 4 }} />
                {hasRevenueCDF && (
                  <Area type="monotone" dataKey="cdf" stroke="#3b82f6" strokeWidth={2} fill="url(#revCdfGrad)" dot={false} activeDot={{ r: 4 }} />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Users */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Utilisateurs récents</h2>
            <Link to="/admin/users" className="text-xs text-emerald-500 hover:text-emerald-600 font-medium flex items-center gap-1">
              Voir tout <ArrowUpRight size={12} />
            </Link>
          </div>
          {recentUsers.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-400">Aucun utilisateur</div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {recentUsers.map(user => (
                <div key={user.id} className="flex items-center gap-3 px-6 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {(user.name || user.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user.name || user.email}</p>
                    <p className="text-xs text-gray-400 truncate">{user.email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                      user.role === 'admin' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                      user.role === 'user' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' :
                      'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {user.role}
                    </span>
                    <p className="text-[10px] text-gray-400 mt-0.5">{formatRelative(user.createdAt)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Categories */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Top catégories</h2>
            <Link to="/admin/categories" className="text-xs text-emerald-500 hover:text-emerald-600 font-medium flex items-center gap-1">
              Voir tout <ArrowUpRight size={12} />
            </Link>
          </div>
          {topCategories.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-400">Aucune catégorie</div>
          ) : (
            <div className="p-6 space-y-3">
              {topCategories.map((cat, i) => (
                <div key={cat.categoryId} className="flex items-center gap-3">
                  <span className="text-xs font-mono text-gray-400 w-4 text-right">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm text-gray-900 dark:text-white truncate">{cat.categoryName}</p>
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 ml-2 shrink-0">{cat.count.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
                      <div
                        className="h-1.5 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
                        style={{ width: `${(cat.count / maxCatCount) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Meilleurs utilisateurs + Examens populaires */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top users (meilleur utilisateur) */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              🏆 Meilleurs utilisateurs
            </h2>
            <Link to="/admin/users" className="text-xs text-emerald-500 hover:text-emerald-600 font-medium flex items-center gap-1">
              Voir tout <ArrowUpRight size={12} />
            </Link>
          </div>
          {topUsers.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-400">Aucun utilisateur</div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {topUsers.map((u, i) => (
                <Link
                  key={u.id}
                  to={`/admin/users/${u.id}`}
                  className="flex items-center gap-3 px-6 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                >
                  <span className="text-sm w-5 text-center shrink-0">
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : <span className="text-gray-400 font-mono text-xs">{i + 1}</span>}
                  </span>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {(u.name || u.email || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{u.name || u.email}</p>
                    <p className="text-xs text-gray-400 truncate">
                      Niv. {u.level} · {u.sessions} session{u.sessions !== 1 ? 's' : ''} · {Math.round(u.averageScore)}%
                    </p>
                  </div>
                  <span className="text-sm font-bold text-amber-600 dark:text-amber-400 shrink-0">
                    {u.totalXp.toLocaleString()} XP
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Examens les plus sollicités */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              🔥 Examens les plus sollicités
            </h2>
            <Link to="/admin/exams" className="text-xs text-emerald-500 hover:text-emerald-600 font-medium flex items-center gap-1">
              Voir tout <ArrowUpRight size={12} />
            </Link>
          </div>
          {popularExams.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-400">Aucune session enregistrée</div>
          ) : (
            <div className="p-6 space-y-3">
              {popularExams.map((ex, i) => {
                const max = popularExams[0]?.sessions || 1;
                return (
                  <div key={ex.examId} className="flex items-center gap-3">
                    <span className="text-xs font-mono text-gray-400 w-4 text-right shrink-0">{i + 1}</span>
                    <span className="text-base shrink-0">{ex.icon || '📄'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm text-gray-900 dark:text-white truncate">{ex.examName}</p>
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 ml-2 shrink-0">
                          {ex.sessions.toLocaleString()} · {ex.uniqueUsers} 👤
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5">
                        <div
                          className="h-1.5 rounded-full bg-gradient-to-r from-orange-400 to-red-500 transition-all"
                          style={{ width: `${(ex.sessions / max) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
        <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
          Actions rapides
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <QuickAction href="/admin/questions/new" icon={FileQuestion} label="Nouvelle question" color="emerald" />
          <QuickAction href="/admin/exams/new" icon={BookOpen} label="Nouvel examen" color="blue" />
          <QuickAction href="/admin/categories/new" icon={FolderOpen} label="Nouvelle catégorie" color="violet" />
          <QuickAction href="/admin/users" icon={Users} label="Gérer utilisateurs" color="amber" />
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color, subtext, trend, trendUp, suffix }: {
  title: string;
  value: number;
  icon: any;
  color: string;
  subtext?: string;
  trend?: string;
  trendUp?: boolean;
  suffix?: string;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1.5">
            {value.toLocaleString()}{suffix || ''}
          </p>
          {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
          {trend && (
            <p className={`text-xs font-medium mt-1.5 flex items-center gap-0.5 ${trendUp ? 'text-emerald-500' : 'text-red-500'}`}>
              {trendUp ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
              {trend}
            </p>
          )}
        </div>
        <div className={`p-2.5 rounded-xl ${color} shrink-0`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  );
}

function ContentCard({ title, value, icon: Icon, href, color }: {
  title: string;
  value: number;
  icon: any;
  href: string;
  color: string;
}) {
  return (
    <Link
      to={href}
      className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow group flex items-center gap-4"
    >
      <div className={`p-3 rounded-xl ${color} shrink-0`}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 dark:text-gray-400">{title}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white">{value.toLocaleString()}</p>
      </div>
      <ArrowUpRight size={16} className="text-gray-300 dark:text-gray-600 group-hover:text-emerald-500 transition-colors shrink-0" />
    </Link>
  );
}

function QuickAction({ href, icon: Icon, label, color }: {
  href: string;
  icon: any;
  label: string;
  color: string;
}) {
  const colors: Record<string, string> = {
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
    blue: 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    violet: 'bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100 dark:hover:bg-violet-900/30 text-violet-600 dark:text-violet-400',
    amber: 'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-600 dark:text-amber-400',
  };
  return (
    <Link
      to={href}
      className={`flex flex-col items-center gap-2 p-4 rounded-xl transition-colors ${colors[color]}`}
    >
      <Icon className="w-7 h-7" />
      <span className="text-xs font-semibold">{label}</span>
    </Link>
  );
}

/** Style commun des tooltips recharts (lisible en clair comme en sombre). */
const TOOLTIP_STYLE: React.CSSProperties = {
  backgroundColor: 'rgba(17,24,39,0.92)',
  border: '1px solid rgba(148,163,184,0.25)',
  borderRadius: '10px',
  fontSize: '12px',
  color: '#fff',
};

/**
 * Série temporelle continue sur les N derniers jours : les jours sans données
 * sont remplis avec `empty` (le backend ne renvoie que les jours actifs).
 */
function fillDailySeries<S extends { date: string }, T extends Record<string, number | null>>(
  days: number,
  points: S[],
  map: (p: S) => T,
  empty: T,
): ({ date: string; label: string } & T)[] {
  const byDate = new Map(points.map((p) => [String(p.date).slice(0, 10), p]));
  const out: ({ date: string; label: string } & T)[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const src = byDate.get(key);
    out.push({
      date: key,
      label: d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
      ...(src ? map(src) : empty),
    });
  }
  return out;
}

function formatRelative(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "à l'instant";
    if (diffMin < 60) return `il y a ${diffMin}min`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `il y a ${diffH}h`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `il y a ${diffD}j`;
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}
